const COLUMN_NAME_TO_FIELD_MAPPING = {
	description: 'description',
	report_id: 'report_name',
	iro_type: 'iro_type',
	page: 'page',
	value_chains: 'value_chains'
}
const REQUIRED_FIELDS = ['report_id', 'page', 'description', 'iro_type', 'value_chains']

/** Mapping for ESRS topic IROs */
const ESRS_COLUMN_NAME_TO_FIELD_MAPPING = {
	...COLUMN_NAME_TO_FIELD_MAPPING,
	esrs_topic: 'esrs_topic',
	esrs_sub_topic: 'esrs_sub_topics'
}
const ESRS_REQUIRED_FIELDS = [...REQUIRED_FIELDS, 'esrs_topic']

/** Mapping for Entity Specific IROs */
const ENTITY_SP_COLUMN_NAME_TO_FIELD_MAPPING = {
	...COLUMN_NAME_TO_FIELD_MAPPING,
	entity_specific_topic: 'entity_specific_topic'
}
const ENTITY_SP_REQUIRED_FIELDS = [...REQUIRED_FIELDS, 'entity_specific_topic']

export class SpreadsheetErrors extends Error {
	constructor(detail = []) {
		super('Spreadsheet import error')
		this.detail = detail
	}
}

/**
 * It checks for required fields and converts values to fit a specific schema.
 * It also handles errors related to missing or invalid data.
 * The class is designed to work with a worksheet that contains IRO data.
 *
 * @class
 */

class IROWorksheetParser {
	_getTextFromRow(row) {
		return row._cells.map((cell) => cell.text || undefined)
	}

	_getColumns(worksheet, requiredFields) {
		const columnHeaders = this._getTextFromRow(worksheet.getRow(1)).map((header) => header?.toLowerCase())
		const missingColumns = requiredFields.filter((column) => !columnHeaders.includes(column.toLowerCase()))

		if (missingColumns.length) {
			throw new SpreadsheetErrors([{ path: 'Mandatory columns', message: missingColumns.join(', ') + ' required' }])
		}
		return columnHeaders
	}

	_notEmpty(row, columns, rowNumber, requiredFields) {
		const emptyColumns = requiredFields.filter((field) => {
			const index = columns.findIndex((col) => col === field)
			if (row[index] === undefined || row[index].trim() === '') {
				return field
			}
		})
		if (emptyColumns.length) {
			throw new SpreadsheetErrors([{ path: `Row ${rowNumber}`, message: emptyColumns.join(', ') + ' cannot be empty' }])
		}
		return true
	}

	_convertFieldsToFitSchema(row, columns, columnToFieldMap) {
		return columns.reduce((acc, value, index) => {
			if (columnToFieldMap[value]) {
				acc[columnToFieldMap[value]] = this._convertValue(row[index])
			}
			return acc
		}, {})
	}

	_convertValue(value) {
		if (value === null || value === undefined || value === '#REF!') {
			return undefined
		}

		if ((value.sharedFormula || value.formula) && value.result === undefined) {
			return undefined
		}
		return value
	}

	_parse(worksheet, requiredFields, columnToFieldMap) {
		const result = []
		const columns = this._getColumns(worksheet, requiredFields)

		worksheet.eachRow((row, index) => {
			const rowText = this._getTextFromRow(row)
			if (index !== 1 && this._notEmpty(rowText, columns, index, requiredFields)) {
				const data = this._convertFieldsToFitSchema(rowText, columns, columnToFieldMap)
				result.push(data)
			}
		})
		return result
	}

	getIROsFromSpreadsheet(worksheets) {
		const tabsToRead = ['IROs', 'Entity-specific IROs']

		const iroTab = worksheets.find((ws) => ws.name === tabsToRead[0])
		const entitySpecificIROTab = worksheets.find((ws) => ws.name === tabsToRead[1])

		// If both tabs are missing raise an error
		if (!iroTab && !entitySpecificIROTab) {
			throw new SpreadsheetErrors([{ path: 'Mandatory tabs', message: `Sheet must contain atleat one of the following tabs: ${tabsToRead.join(', ')}` }])
		}
		return {
			iros: iroTab ? this._parse(iroTab, ESRS_REQUIRED_FIELDS, ESRS_COLUMN_NAME_TO_FIELD_MAPPING) : [],
			entity_specific_iros: entitySpecificIROTab ? this._parse(entitySpecificIROTab, ENTITY_SP_REQUIRED_FIELDS, ENTITY_SP_COLUMN_NAME_TO_FIELD_MAPPING) : []
		}
	}
}

export default new IROWorksheetParser()
